/**
 * useScheduleGridDisplay.ts
 * Persisted display preferences for the Machine Schedule grids (standalone page
 * + Scenario Planning embed):
 *
 *   - dayColWidth  — continuous day-column width in px ("Excel-like" zoom:
 *                    S/M/L presets, +/- steps, Ctrl+wheel over the grid).
 *   - compactRows  — one-line machine labels ("code · name") at roughly half
 *                    the row height, so ~2× the machines fit on screen.
 *
 * Both persist in localStorage under shared keys so every view that opts in
 * stays consistent. Defaults follow the 2026-07 sticky-headers UX pass:
 * compact ON, columns slightly narrower than the old DAY_COL_MIN.
 */

import { useCallback, useState } from 'react';

const WIDTH_KEY = 'moftaki.scheduleGrid.dayColWidth';
const COMPACT_KEY = 'moftaki.scheduleGrid.compactRows';

export const DAY_COL_WIDTH_MIN = 22;
export const DAY_COL_WIDTH_MAX = 56;
export const DAY_COL_WIDTH_DEFAULT = 30;
/** +/- button zoom step, px (Ctrl+wheel steps ±2 for finer control). */
export const DAY_COL_WIDTH_STEP = 4;

export function clampDayColWidth(px: number): number {
  return Math.min(DAY_COL_WIDTH_MAX, Math.max(DAY_COL_WIDTH_MIN, Math.round(px)));
}

function loadWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? clampDayColWidth(n) : DAY_COL_WIDTH_DEFAULT;
  } catch {
    return DAY_COL_WIDTH_DEFAULT;
  }
}

function loadCompact(): boolean {
  try {
    const raw = localStorage.getItem(COMPACT_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export interface ScheduleGridDisplay {
  dayColWidth: number;
  setDayColWidth: (px: number) => void;
  /** Relative zoom (Ctrl+wheel / +/- buttons); clamps to [MIN, MAX]. */
  zoomDayColWidth: (deltaPx: number) => void;
  compactRows: boolean;
  setCompactRows: (compact: boolean) => void;
}

export function useScheduleGridDisplay(): ScheduleGridDisplay {
  const [dayColWidth, setWidthState] = useState<number>(loadWidth);
  const [compactRows, setCompactState] = useState<boolean>(loadCompact);

  const persistWidth = (px: number) => {
    try {
      localStorage.setItem(WIDTH_KEY, String(px));
    } catch {
      /* ignore quota / private-mode errors */
    }
  };

  const setDayColWidth = useCallback((px: number) => {
    const v = clampDayColWidth(px);
    setWidthState(v);
    persistWidth(v);
  }, []);

  const zoomDayColWidth = useCallback((deltaPx: number) => {
    setWidthState((prev) => {
      const v = clampDayColWidth(prev + deltaPx);
      persistWidth(v);
      return v;
    });
  }, []);

  const setCompactRows = useCallback((compact: boolean) => {
    setCompactState(compact);
    try {
      localStorage.setItem(COMPACT_KEY, String(compact));
    } catch {
      /* ignore */
    }
  }, []);

  return { dayColWidth, setDayColWidth, zoomDayColWidth, compactRows, setCompactRows };
}
